'use client';

import { FiArrowDown, FiSend } from 'react-icons/fi';
import {
  ReactElement,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';

import Image from 'next/image';
import { ChatHeader } from './ChatWindowHeader';
import {
  ChatMessageBubble,
  TRAINME_DEFAULT_COACH_AVATAR_URL,
} from '@/app/chat/ChatMessageBubble';
import { IntermediateStep } from '@/app/chat/IntermediateStep';
import Loading from '@/app/components/Loading';
import { Message } from 'ai';
import React from 'react';
import { cn } from '@/utils/uiUtils';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { useChat } from '@ai-sdk/react';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useCoachChatStore } from '@/app/stores/coachChatStore';
import { useSettingsStore } from '@/app/stores/settingsStore';
import { useUser } from '@clerk/nextjs';
import { scrollMessageIntoView } from '@/app/chat/scrollToMessage';

export interface Source {
  id: string;
  name: string;
  metadata: {
    loc: {
      lines: { from: number; to: number };
    };
  };
}

const COACH_MESSAGES = ['Thinking', 'Just a moment'];

export interface ChatWindowHandle {
  refreshMessages: () => Promise<void>;
}

export interface ChatWindowProps {
  endpoint: string;
  showIntermediateStepsToggle?: boolean;
  closeButton?: ReactElement;
  className?: string;
  showHeader?: boolean;
}

export const ChatWindow = React.forwardRef<ChatWindowHandle, ChatWindowProps>(
  function ChatWindow(props, ref) {
    const {
      endpoint,
      closeButton,
      className,
      showIntermediateStepsToggle,
      showHeader = true,
    } = props;

    const endOfMessageRef = useRef<HTMLDivElement | null>(null);
    const messageContainerRef = useRef<HTMLDivElement | null>(null);

    const [typingMode, setTypingMode] = useState<'coach' | 'general'>('coach');

    const { user } = useUser();
    const greeting = user?.firstName
      ? `Hi ${user.firstName}! Welcome back, how are you doing today?`
      : 'Hi, I am your AI coach. Ask me anything.';

    const enableIntermediateSteps = showIntermediateStepsToggle ?? true;
    const showThoughts = true;
    const [intermediateStepsLoading, setIntermediateStepsLoading] =
      useState(false);

    const [sourcesForMessages, setSourcesForMessages] = useState<
      Record<string, Source[]>
    >({});

    const showIntermediateSteps = enableIntermediateSteps;
    const [, setChatId] = useState<string | null>(null);
    const [isLoadingMessages, setIsLoadingMessages] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const currentStatus = useCoachChatStore((s) => s.currentStatus);
    const clearCurrentStatus = useCoachChatStore((s) => s.clearCurrentStatus);
    const scrollToMessageId = useCalendarState((s) => s.chatScrollToMessageId);
    const setChatScrollToMessageId = useCalendarState(
      (s) => s.setChatScrollToMessageId
    );

    const detectedTimezone = useMemo(() => {
      if (typeof Intl === 'undefined') {
        return undefined;
      }
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz || undefined;
      } catch (error) {
        console.warn('Failed to detect timezone from Intl:', error);
        return undefined;
      }
    }, []);

    const {
      messages,
      input,
      setInput,
      handleInputChange,
      handleSubmit,
      isLoading: chatEndpointIsLoading,
      setMessages,
      append,
    } = useChat({
      api: endpoint,
      body: detectedTimezone ? { timezone: detectedTimezone } : undefined,
      initialMessages: [],
      onResponse(response: Response) {
        const sourcesHeader = response.headers.get('x-sources');
        const sources = sourcesHeader ? JSON.parse(atob(sourcesHeader)) : [];
        const messageIndexHeader = response.headers.get('x-message-index');
        if (sources.length && messageIndexHeader !== null) {
          setSourcesForMessages({
            ...sourcesForMessages,
            [messageIndexHeader]: sources,
          });
        }
      },
      onError: (e: Error) => {
        // Ignore false-alarm protocol parsing errors for non-streamed JSON payloads
        if (e.message.includes('Failed to parse stream string')) {
          console.warn(
            '[ChatWindow] Suppressed protocol parsing error:',
            e.message
          );
          return;
        }
        toast.error('Failed to send message: ' + e.message);
      },
      onFinish: async (_message: Message) => {
        // Assistant messages are now saved by the server during streaming/completion
      },
    });

    const onAction = React.useCallback(
      (content: string) => {
        append({ role: 'user', content });
      },
      [append]
    );

    useEffect(() => {
      if (!chatEndpointIsLoading && !intermediateStepsLoading) {
        startTransition(() => {
          clearCurrentStatus();
        });
      }
    }, [chatEndpointIsLoading, intermediateStepsLoading, clearCurrentStatus]);

    const scrollToBottom = () => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    };

    // Auto-scroll when messages change
    useEffect(() => {
      const container = messageContainerRef.current;
      if (!container) return;

      // In flex-col-reverse, visual bottom is DOM top (scrollTop = 0)
      // We auto-scroll if the user is already near the bottom (within 100px)
      if (Math.abs(container.scrollTop) < 100) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, [messages]);

    useEffect(() => {
      const container = messageContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        // In flex-col-reverse, scrollTop is 0 at the bottom and increases as you scroll up
        const isScrolledUp = Math.abs(container.scrollTop) > 100;
        setShowScrollButton(isScrolledUp);
      };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    const loadMessages = React.useCallback(
      async (isInitial = false) => {
        try {
          if (isInitial) {
            setIsLoadingMessages(true);
          }

          let url = '/api/chat/messages?limit=20';

          // Migration logic
          const localMessagesStr = localStorage.getItem('chatMessages');
          const coachChatId = localStorage.getItem('coachChatId');
          const isMigrated = localStorage.getItem('coachChatMigrated');

          if (localMessagesStr && !coachChatId && !isMigrated) {
            try {
              // Validate it's parseable
              JSON.parse(localMessagesStr);
              url += `&migrateMessages=${encodeURIComponent(localMessagesStr)}`;
            } catch (e) {
              console.error('Invalid local messages for migration', e);
            }
          }

          const response = await fetch(url, {
            signal: AbortSignal.timeout(10000), // 10 second timeout
            cache: 'no-store',
          });

          if (!response.ok) {
            throw new Error('Failed to load messages');
          }

          const data = await response.json();
          const {
            messages: loadedMessages,
            chatId: loadedChatId,
            nextCursor: initialNextCursor,
          } = data;

          if (loadedMessages && Array.isArray(loadedMessages)) {
            setMessages(loadedMessages);
            setNextCursor(initialNextCursor || null);

            if (loadedChatId) {
              setChatId(loadedChatId);
              localStorage.setItem('coachChatId', loadedChatId);

              // Cleanup migration
              if (localMessagesStr) {
                localStorage.removeItem('chatMessages');
                localStorage.setItem('coachChatMigrated', 'true');
              }
            }
            return loadedMessages.length;
          }
          return 0;
        } catch (error) {
          if (error instanceof Error && error.name === 'TimeoutError') {
            console.warn('[ChatWindow] Message loading timed out after 10s');
            toast.error(
              'Failed to load messages: Request timed out. Please try refreshing.'
            );
          } else {
            console.error('Failed to load messages from database:', error);
          }
          return 0;
        } finally {
          if (isInitial) {
            setIsLoadingMessages(false);
          }
        }
      },
      [setMessages]
    );

    const refreshMessages = async () => {
      await loadMessages(false);
      toast.success('Messages refreshed');
    };

    useImperativeHandle(ref, () => ({ refreshMessages }), [refreshMessages]);

    // once a target message is requested and messages are loaded,
    // try to scroll it into view. Retries as messages/loading state change
    // (e.g. arriving async) but stays a graceful no-op forever if the id
    // never matches anything -- it's cleared either way so we don't keep
    // retrying indefinitely once messages have settled.
    useEffect(() => {
      if (!scrollToMessageId || isLoadingMessages) {
        return;
      }
      const found = scrollMessageIntoView(
        messageContainerRef.current,
        scrollToMessageId
      );
      if (found || messages.length > 0) {
        setChatScrollToMessageId(null);
      }
    }, [
      scrollToMessageId,
      isLoadingMessages,
      messages,
      setChatScrollToMessageId,
    ]);

    const refreshChatCounter = useCoachChatStore((s) => s.refreshChat);
    const prevRefreshCounterRef = useRef(refreshChatCounter);
    const utils = trpc.useUtils();

    // Load messages from database on mount OR when counter changes
    useEffect(() => {
      // Check if we are coming from a vendor connection redirect
      const urlParams = new URLSearchParams(window.location.search);
      const cameFromVendorConnect =
        urlParams.has('strava_connected') || urlParams.has('garmin_connected');

      if (cameFromVendorConnect) {
        // Invalidate connection status to ensure UI updates
        utils.strava.connected.invalidate();
        utils.garmin.connected.invalidate();
      }

      const isInitialLoad = refreshChatCounter === 0;
      const isRefreshTriggered =
        refreshChatCounter > 0 &&
        refreshChatCounter !== prevRefreshCounterRef.current;

      if (isInitialLoad || isRefreshTriggered) {
        prevRefreshCounterRef.current = refreshChatCounter;
        loadMessages(isInitialLoad);
      }
    }, [loadMessages, refreshChatCounter, utils]);

    const loadMoreMessages = React.useCallback(async () => {
      if (!nextCursor || isFetchingMore || chatEndpointIsLoading) return;

      try {
        setIsFetchingMore(true);
        const response = await fetch(
          `/api/chat/messages?limit=20&cursor=${nextCursor}`,
          {
            signal: AbortSignal.timeout(10000), // 10 second timeout
            cache: 'no-store',
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load more messages');
        }

        const data = await response.json();
        const { messages: olderMessages, nextCursor: newNextCursor } = data;

        if (olderMessages && Array.isArray(olderMessages)) {
          setMessages((prevMessages: Message[]) => [
            ...olderMessages,
            ...prevMessages,
          ]);
        }

        setNextCursor(newNextCursor || null);
      } catch (error) {
        if (error instanceof Error && error.name === 'TimeoutError') {
          console.warn('[ChatWindow] Load more messages timed out after 10s');
          toast.error('Failed to load more messages: Request timed out.');
        } else {
          console.error('Failed to load more messages:', error);
        }
      } finally {
        setIsFetchingMore(false);
      }
    }, [nextCursor, isFetchingMore, chatEndpointIsLoading, setMessages]);

    // Infinite scroll observer
    useEffect(() => {
      if (!nextCursor || isFetchingMore || isLoadingMessages) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreMessages();
          }
        },
        {
          threshold: 0,
          rootMargin: '400px',
        }
      );

      const currentTrigger = loadMoreTriggerRef.current;
      if (currentTrigger) {
        observer.observe(currentTrigger);
      }

      return () => {
        if (currentTrigger) {
          observer.unobserve(currentTrigger);
        }
      };
    }, [nextCursor, isFetchingMore, isLoadingMessages, loadMoreMessages]);

    const normalizeAssistantContent = (content: unknown) => {
      if (typeof content === 'string') {
        return content;
      }

      if (Array.isArray(content)) {
        return content
          .map((segment) => {
            if (segment == null) return '';
            if (typeof segment === 'string') return segment;
            if (typeof segment === 'object') {
              const text = (segment as Record<string, any>)?.text;
              if (text && typeof text.value === 'string') {
                return text.value;
              }
            }
            return JSON.stringify(segment);
          })
          .join('');
      }

      if (typeof content === 'object') {
        const maybeText = (content as Record<string, any>)?.text;
        if (maybeText && typeof maybeText.value === 'string') {
          return maybeText.value;
        }
      }

      return JSON.stringify(content);
    };

    async function showIntermediateMessage() {
      setIntermediateStepsLoading(true);
      setInput(' ');
      const messagesWithUserReply = messages.concat({
        id: crypto.randomUUID(),
        content: input,
        role: 'user',
      });
      setMessages(messagesWithUserReply);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messagesWithUserReply,
          show_intermediate_steps: true,
        }),
      });
      const contentType = response.headers.get('content-type') || '';
      const json = contentType.includes('application/json')
        ? await response.json()
        : { messages: [{ role: 'assistant', content: await response.text() }] };
      setIntermediateStepsLoading(false);
      if (response.status === 200) {
        let responseMessages: Message[] = Array.isArray(json.messages)
          ? json.messages
          : [];

        // Support V3 response format where the response itself is the message
        if (
          responseMessages.length === 0 &&
          json &&
          typeof json === 'object' &&
          'schema_version' in json
        ) {
          responseMessages = [
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              content: JSON.stringify(json),
            },
          ];
        }

        const toolCallMessages = responseMessages.filter(
          (responseMessage: Message) => {
            const responseRole = responseMessage.role as string;
            return (
              (responseRole === 'assistant' &&
                !!responseMessage.tool_calls?.length) ||
              responseRole === 'tool'
            );
          }
        );
        const intermediateStepMessages = [];
        for (let i = 0; i < toolCallMessages.length; i += 2) {
          const aiMessage = toolCallMessages[i];
          const toolMessage = toolCallMessages[i + 1];
          intermediateStepMessages.push({
            id: crypto.randomUUID(),
            role: 'system' as const,
            content: JSON.stringify({
              action: aiMessage.tool_calls?.[0],
              observation: toolMessage?.content,
            }),
          });
        }
        const newMessages = messagesWithUserReply;
        for (const message of intermediateStepMessages) {
          newMessages.push(message);
          setMessages([...newMessages]);
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 + Math.random() * 1000)
          );
        }
        const finalResponseMessage = responseMessages.at(-1);
        const finalContent = finalResponseMessage?.content ?? '';
        const finalAssistantMessage = {
          id: crypto.randomUUID(),
          content: normalizeAssistantContent(finalContent),
          role: 'assistant' as const,
        };
        setMessages([...newMessages, finalAssistantMessage]);
      } else {
        if (json.error) {
          toast.error('Failed to send message: ' + json.error);
          throw new Error(json.error);
        }
      }
    }

    async function sendMessage(e: React.FormEvent<HTMLFormElement>) {
      e.preventDefault();
      if (input.trim() === '') {
        return;
      }

      const fitnessKeywords = [
        'workout',
        'run',
        'plan',
        'train',
        'exercise',
        'activity',
        'strava',
        'garmin',
        'heart',
        'pace',
        'km',
        'mile',
        'nutrition',
        'eat',
        'food',
        'weight',
        'zone',
        'recovery',
        'sleep',
        'stress',
        'vo2',
        'threshold',
      ];
      const isGeneral = !fitnessKeywords.some((k) =>
        input.toLowerCase().includes(k)
      );
      setTypingMode(isGeneral ? 'general' : 'coach');

      if (messageContainerRef.current) {
        messageContainerRef.current.classList.add('grow');
      }

      // Immediate scroll on send
      scrollToBottom();

      if (chatEndpointIsLoading || intermediateStepsLoading) {
        return;
      } else if (showIntermediateSteps) {
        await showIntermediateMessage();
      } else {
        handleSubmit();
      }
    }

    return (
      <div
        className={cn([
          'h-full overflow-hidden flex flex-col justify-between pb-0 bg-gray-300 dark:bg-gray-700',
          className,
        ])}
      >
        {showHeader && (
          <div
            className={cn([
              'w-full flex-0 flex justify-between',
              'bg-gradient-to-b from-slate-400 to-slate-300 dark:from-slate-600 dark:to-slate-500',
            ])}
          >
            <ChatHeader refreshMessages={refreshMessages} />
            {closeButton}
          </div>
        )}
        <div
          id='chat-content'
          className='flex-1 p-3 md:p-4 flex flex-col-reverse w-full mb-0 transition-[flex-grow] ease-in-out overflow-y-auto scroll'
          ref={messageContainerRef}
        >
          {/* Visual bottom is DOM top in flex-col-reverse */}
          <div ref={endOfMessageRef} />
          {isLoadingMessages ? (
            <div className='flex items-center justify-center h-full'>
              <Loading />
            </div>
          ) : (
            <>
              {[...messages].reverse().map((m, i) => {
                const sourceKey = (messages.length - 1 - i).toString();
                const messageRole = m.role as string;
                if (
                  messageRole === 'system' ||
                  messageRole === 'tool' ||
                  messageRole === 'function'
                ) {
                  return (
                    <div key={m.id} data-message-id={m.id}>
                      <IntermediateStep
                        message={m}
                        show={showIntermediateSteps}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div key={m.id} data-message-id={m.id}>
                      <ChatMessageBubble
                        message={m}
                        onAction={onAction}
                        sources={
                          sourcesForMessages[sourceKey]?.map((source) => ({
                            id: source.id,
                            name: source.name,
                            metadata: source.metadata,
                          })) || []
                        }
                      />
                    </div>
                  );
                }
              })}
              {/* Infinite scroll trigger */}
              {nextCursor && (
                <div
                  ref={loadMoreTriggerRef}
                  className='w-full flex justify-center py-8 items-center min-h-[80px]'
                >
                  {isFetchingMore ? (
                    <SequentialDots
                      ariaLabel='loading more messages'
                      color='#fbbf24'
                      dotSize={6}
                      gap={6}
                    />
                  ) : (
                    <div className='w-full h-1' />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {showScrollButton && (
          <div className='absolute bottom-24 left-0 right-0 flex justify-center z-[999] pointer-events-none'>
            <button
              onClick={scrollToBottom}
              className='p-3 bg-white/40 dark:bg-slate-800/40 backdrop-blur-sm text-slate-900 dark:text-slate-100 rounded-full shadow-xl hover:bg-white/60 dark:hover:bg-slate-700/60 transition-all cursor-pointer animate-bounce border border-slate-200/50 dark:border-slate-700/50 pointer-events-auto'
              aria-label='Scroll to bottom'
            >
              <FiArrowDown size={24} />
            </button>
          </div>
        )}

        {(chatEndpointIsLoading || intermediateStepsLoading) && (
          <AIThinking
            key={typingMode}
            messages={COACH_MESSAGES}
            showThoughts={showThoughts}
            currentStatus={currentStatus}
          />
        )}
        <div className='flex-0 chat-footer w-full bg-slate-300 dark:bg-slate-700 relative'>
          <form
            onSubmit={sendMessage}
            className='flex w-full flex-col'
            autoComplete='off'
          >
            <div className='relative flex w-full gap-0 items-stretch rounded-none group'>
              <span className='pointer-events-none absolute inset-x-0 top-0 h-px bg-slate-400/40 dark:bg-slate-600/40' />
              <span className='pointer-events-none absolute inset-x-0 top-0 h-px bg-yellow-200/10 dark:bg-yellow-300/10' />
              <span className='pointer-events-none absolute inset-x-0 top-0 h-px bg-yellow-400 opacity-0 transition-opacity duration-200 group-focus-within:opacity-100' />
              <input
                id='chat-input'
                className={cn([
                  'flex-1 px-3 py-1.5 pr-12 min-h-[40px] rounded-none',
                  'bg-gray-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  'focus:outline-none focus:ring-0',
                ])}
                value={input}
                placeholder={greeting}
                onChange={handleInputChange}
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck='false'
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const form = e.currentTarget.form;
                    if (form) {
                      form.dispatchEvent(
                        new Event('submit', { cancelable: true, bubbles: true })
                      );
                    }
                  }
                }}
              />
              <button
                type='submit'
                className='btn btn-primary rounded-none min-h-[48px] min-w-[48px] h-full flex items-center justify-center p-0 border-l border-slate-300 dark:border-slate-600'
              >
                {chatEndpointIsLoading || intermediateStepsLoading ? (
                  <div role='status' className='flex justify-center'>
                    <span className='sr-only'>Loading...</span>
                    <Loading />
                  </div>
                ) : (
                  <FiSend className='w-4 h-4 md:w-6 md:h-6' />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

type SequentialDotsProps = {
  dotCount?: number;
  dotSize?: number;
  gap?: number;
  color?: string;
  speed?: number;
  className?: string;
  ariaLabel?: string;
  offsetY?: number;
};

const SequentialDots = ({
  dotCount = 3,
  dotSize = 6,
  gap = 5,
  color = 'currentColor',
  speed = 450,
  className = '',
  ariaLabel = 'loading',
  offsetY = 0,
}: SequentialDotsProps) => {
  const [visibleDots, setVisibleDots] = useState(0);

  useEffect(() => {
    const totalStages = dotCount + 1;
    const timer = setInterval(() => {
      setVisibleDots((prev) => (prev + 1) % totalStages);
    }, speed);

    return () => clearInterval(timer);
  }, [dotCount, speed]);

  return (
    <span
      role='status'
      aria-label={ariaLabel}
      className={cn('inline-flex items-center justify-center', className)}
      style={{ transform: offsetY ? `translateY(${offsetY}px)` : undefined }}
    >
      {Array.from({ length: dotCount }).map((_, idx) => (
        <span
          key={idx}
          className={cn(
            'rounded-full transition-opacity duration-150 ease-in-out',
            {
              'opacity-100': idx < visibleDots,
              'opacity-0': idx >= visibleDots,
            }
          )}
          style={{
            width: dotSize,
            height: dotSize,
            marginLeft: idx === 0 ? 0 : gap,
            backgroundColor: color,
          }}
        />
      ))}
    </span>
  );
};

const AIThinking = ({
  className,
  messages = ['Thinking...'],
  showThoughts,
  currentStatus,
}: {
  className?: string;
  messages?: string[];
  showThoughts: boolean;
  currentStatus: string | null;
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const coachProfile = useSettingsStore(
    (state) => state.trainingSettings?.coachProfile
  );

  const hasHighResMessage = showThoughts && Boolean(currentStatus);

  useEffect(() => {
    if (hasHighResMessage) {
      return;
    }

    if (!messages.length) {
      return;
    }

    if (currentMessageIndex >= messages.length - 1) return;

    const nextDelay = 4000 + Math.random() * 3000;
    const timer = setTimeout(() => {
      setCurrentMessageIndex((prev) => prev + 1);
    }, nextDelay);

    return () => clearTimeout(timer);
  }, [currentMessageIndex, hasHighResMessage, messages]);

  const displayMessage =
    hasHighResMessage && currentStatus
      ? currentStatus
      : messages[currentMessageIndex];

  return (
    <div
      className={cn([
        'mx-8 py-2 flex gap-4 items-center justify-start h-fit bg-transparent',
        className,
      ])}
    >
      <span className='flex items-center justify-center w-5 h-5 md:w-8 md:h-8 rounded-full overflow-hidden border border-yellow-300/70'>
        <Image
          src={coachProfile?.avatarUrl ?? TRAINME_DEFAULT_COACH_AVATAR_URL}
          alt={coachProfile?.name ?? 'AI Coach'}
          width={32}
          height={32}
          className='w-5 h-5 md:w-8 md:h-8 object-cover'
        />
      </span>
      <div className='text-slate-900 dark:text-slate-100 flex gap-2 items-center transition-all duration-500'>
        <span>{displayMessage}</span>
        <SequentialDots
          ariaLabel='coach typing'
          dotSize={5}
          gap={4}
          color='currentColor'
          offsetY={1}
        />
      </div>
    </div>
  );
};
